import { useMemo } from 'react';

// Hook pour gérer les permissions basées sur le statut du tenant
export const useTenantStatus = (tenant) => {
    return useMemo(() => {
        if (!tenant) return { 
            status: 'suspended', 
            canCreateMessage: false, 
            canCreateTodo: false,
            canInviteUser: false,
            canExport: false,
            canImport: false,
            canUpgrade: false,
            isRestricted: true,
            restrictions: ['Tenant non trouvé'],
            // Helpers
            isActive: false,
            isSuspended: true,
            isCancelled: false,
            // Messages
            getStatusMessage: () => 'Tenant non trouvé',
            getActionMessage: (action) => 'Tenant non trouvé - Accès restreint'
        };

        const status = tenant.status || 'active';
        const restrictions = [];

        // Définir les permissions selon le statut
        let canCreateMessage = true;
        let canCreateTodo = true;
        let canInviteUser = true;
        let canExport = true;
        let canImport = true;
        let canUpgrade = true;
        let isRestricted = false;

        switch (status) {
            case 'suspended':
                canCreateMessage = false;
                canCreateTodo = false;
                // canInviteUser = false; // Laisser les invitations actives pour organisations suspendues
                canExport = false;
                canImport = false;
                isRestricted = true;
                restrictions.push(
                    'Création de messages désactivée',
                    'Création de todos désactivée',
                    // 'Invitations désactivées', // Invitations restent actives
                    'Export/Import désactivés'
                );
                break;
                
            case 'active':
            default:
                // Tout est autorisé selon les limites du plan
                break;
        }

        // Vérifier aussi les limites du plan
        const usage = tenant.usage || {};
        const limits = tenant.limits || {};

        if (limits.maxMessages && usage.messages >= limits.maxMessages) {
            canCreateMessage = false;
            restrictions.push('Limite de messages atteinte');
        }

        if (limits.maxTodos && usage.todos >= limits.maxTodos) {
            canCreateTodo = false;
            restrictions.push('Limite de todos atteinte');
        }

        if (limits.maxUsers && usage.users >= limits.maxUsers) {
            canInviteUser = false;
            restrictions.push('Limite d\'utilisateurs atteinte');
        }

        return {
            status,
            canCreateMessage,
            canCreateTodo,
            canInviteUser,
            canExport,
            canImport,
            canUpgrade,
            isRestricted: isRestricted || restrictions.length > 0,
            restrictions,
            // Helpers
            isActive: status === 'active',
            isSuspended: status === 'suspended',
            // Messages
            getStatusMessage: () => {
                switch (status) {
                    case 'suspended':
                        return 'Problème de paiement - Accès limité';
                    case 'active':
                    default:
                        return 'Compte actif';
                }
            },
            getActionMessage: (action) => {
                if (!isRestricted) return null;
                
                switch (action) {
                    case 'create_message':
                        return canCreateMessage ? null : 'Création de messages non disponible';
                    case 'create_todo':
                        return canCreateTodo ? null : 'Création de todos non disponible';
                    case 'invite_user':
                        return canInviteUser ? null : 'Invitations non disponibles';
                    case 'export':
                        return canExport ? null : 'Export non disponible';
                    case 'import':
                        return canImport ? null : 'Import non disponible';
                    case 'upgrade':
                        return canUpgrade ? null : 'Mise à niveau non disponible';
                    default:
                        return restrictions.length > 0 ? restrictions[0] : null;
                }
            }
        };
    }, [tenant]);
};

// Hook pour vérifier si une action est autorisée
export const useCanPerformAction = (tenant, action) => {
    const status = useTenantStatus(tenant);
    
    return useMemo(() => {
        switch (action) {
            case 'create_message':
                return status.canCreateMessage;
            case 'create_todo':
                return status.canCreateTodo;
            case 'invite_user':
                return status.canInviteUser;
            case 'export':
                return status.canExport;
            case 'import':
                return status.canImport;
            case 'upgrade':
                return status.canUpgrade;
            default:
                return !status.isRestricted;
        }
    }, [status, action]);
};

export default useTenantStatus;